import React, { useState, useRef, useEffect, useCallback } from 'react';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useConceptGate } from '../../../context/ConceptGateContext';

const ACCOUNT_COLLAPSED_KEY = 'ntb:conceptgate:accountCollapsed';

const AccountSection = ({ account, onEdit, editable, isExpanded, onToggle }) => {
  const { updateAccount } = useConceptGate();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [localEquity, setLocalEquity] = useState(account?.equity || 100000);

  // Sync local equity with account
  useEffect(() => {
    setLocalEquity(account?.equity || 100000);
  }, [account?.equity]);

  // Collapse state - use controlled props if provided (accordion mode), otherwise use internal state
  const [internalCollapsed, setInternalCollapsed] = useState(() => {
    const saved = localStorage.getItem(ACCOUNT_COLLAPSED_KEY);
    // Default to true if not set, or if set to true
    return saved === null ? true : saved === 'true';
  });

  const inputRef = useRef(null);

  // Scrubber state
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startValue = useRef(0);

  // Use controlled state if provided (accordion mode), otherwise use internal state
  const isCollapsed = isExpanded !== undefined ? !isExpanded : internalCollapsed;

  // Persist collapse state only when using internal state
  useEffect(() => {
    if (isExpanded === undefined) {
      localStorage.setItem(ACCOUNT_COLLAPSED_KEY, internalCollapsed.toString());
    }
  }, [internalCollapsed, isExpanded]);

  const toggleCollapse = () => {
    if (onToggle) {
      // Controlled mode (accordion)
      onToggle();
    } else {
      // Internal state mode (backward compatibility)
      setInternalCollapsed(!internalCollapsed);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: account?.currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleStartEdit = () => {
    setEditValue((account?.equity || 100000).toString());
    setIsEditing(true);
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    const numValue = parseFloat(editValue.replace(/[^0-9.-]/g, ''));
    if (!isNaN(numValue) && numValue > 0) {
      await updateAccount({ equity: numValue });
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // Scrubber handlers - inline to avoid callback timing issues
  const handleScrubStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    isDragging.current = true;
    startX.current = e.clientX;
    startValue.current = localEquity;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';

    const handleMouseMove = (moveE) => {
      if (!isDragging.current) return;
      const deltaX = moveE.clientX - startX.current;
      // $100 per pixel
      const deltaValue = deltaX * 100;
      const newValue = Math.max(0, Math.round(startValue.current + deltaValue));
      setLocalEquity(newValue);
    };

    const handleMouseUp = async () => {
      if (isDragging.current) {
        isDragging.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';

        // Get current local equity value
        const currentLocalEquity = document.querySelector('.account-equity-scrubber, .scrubber-value')?.dataset?.equity;
        if (currentLocalEquity) {
          await updateAccount({ equity: parseInt(currentLocalEquity, 10) });
        }
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [localEquity, updateAccount]);

  // Save when local equity changes after drag
  const lastSavedEquity = useRef(localEquity);
  useEffect(() => {
    // Debounce save - only save after dragging stops
    if (localEquity !== lastSavedEquity.current && !isDragging.current) {
      const timeout = setTimeout(async () => {
        if (localEquity !== account?.equity) {
          await updateAccount({ equity: localEquity });
          lastSavedEquity.current = localEquity;
        }
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [localEquity, account?.equity, updateAccount]);

  const equityValue = formatCurrency(localEquity);

  return (
    <div className={`conceptgate-section conceptgate-account-section ${isCollapsed ? 'collapsed' : ''}`}>
      {/* Collapsed View - Always rendered, shown when collapsed */}
      <div 
        className={`conceptgate-section-header collapsed-header ${isCollapsed ? '' : 'hidden'}`} 
        onClick={toggleCollapse} 
        style={{ cursor: 'pointer' }}
      >
        <AccountBalanceWalletIcon fontSize="small" />
        <span>Account</span>

        {/* Summary stats - scrubable */}
        <div
          className="account-equity-scrubber"
          data-equity={localEquity}
          onMouseDown={editable ? handleScrubStart : undefined}
          onClick={(e) => e.stopPropagation()}
          title={editable ? "Drag ↔ to adjust" : undefined}
          style={editable ? { cursor: 'ew-resize' } : undefined}
        >
          {equityValue} {account?.currency || 'USD'}
          {editable && <span className="scrub-indicator">↔</span>}
        </div>

        <span className="collapse-toggle" title="Expand">
          <ExpandMoreIcon fontSize="small" />
        </span>
      </div>

      {/* Expanded View - Always rendered, shown when expanded */}
      <div className={`expanded-view ${isCollapsed ? 'hidden' : ''}`}>
        <div className="conceptgate-section-header" onClick={toggleCollapse} style={{ cursor: 'pointer' }}>
          <AccountBalanceWalletIcon fontSize="small" />
          <span>Account</span>
          <span className="collapse-toggle" title="Collapse" style={{ marginLeft: 'auto' }}>
            <ExpandLessIcon fontSize="small" />
          </span>

          {onEdit && !editable && (
            <button
              className="conceptgate-header-edit-btn"
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              title="Edit account"
            >
              <EditIcon fontSize="small" />
            </button>
          )}
        </div>
        <div className="conceptgate-section-content">
            <div className="conceptgate-field-row">
              <span className="conceptgate-field-label">Equity</span>
              {isEditing ? (
                <div className="conceptgate-inline-edit">
                  <input
                    ref={inputRef}
                    type="text"
                    inputMode="decimal"
                    className="conceptgate-inline-input"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleSave}
                  />
                  <button className="conceptgate-inline-btn save" onClick={handleSave}>
                    <CheckIcon fontSize="small" />
                  </button>
                  <button className="conceptgate-inline-btn cancel" onClick={handleCancel}>
                    <CloseIcon fontSize="small" />
                  </button>
                </div>
              ) : (
                <span
                  className={`conceptgate-field-value scrubber-value ${editable ? 'editable' : ''}`}
                  data-equity={localEquity}
                  onMouseDown={editable ? handleScrubStart : undefined}
                  onClick={editable ? handleStartEdit : undefined}
                  title={editable ? 'Click to edit, drag ↔ to adjust' : undefined}
                  style={editable ? { cursor: 'ew-resize' } : undefined}
                >
                  {equityValue}
                  {editable && <span className="scrub-indicator">↔</span>}
                </span>
              )}
            </div>
            <div className="conceptgate-field-row">
              <span className="conceptgate-field-label">Currency</span>
              <span className="conceptgate-field-value">
                {account?.currency || 'USD'}
              </span>
            </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSection;
